/**
 * Management-dashboard DTO's — marge per pakket, bezettingsgraad, omzet/kosten/
 * winst-prognose en capaciteit per medewerker.
 *
 * Conform docs/specs/20-calculatie-offerte-tool.md §2.5 + §2.6. De pure
 * berekeningen leven in `@streep/utils` (`management-calc.ts`); deze module
 * levert alleen de over-de-API-grens DTO-vorm. Gevoelige financiële data —
 * de API gate't op rol ADMIN/MANAGER (spec §2.5 + §5.2).
 *
 * Bedragen/uren als `number` (DB rekent op Decimal; API serialiseert).
 */

import { z } from 'zod'

// ─── A. Marge per pakket ──────────────────────────────────────────────────────

export interface PackageMarginRowDTO {
  key: string
  label: string
  sellRate: number
  avgMonteurCost: number
  marginPerHour: number
  marginPercentage: number
  negative: boolean
}

export interface MarginPerPackageDTO {
  /** Gemiddelde monteur-kostprijs/uur (STORING + ONDERHOUD), basis voor marge. */
  avgMonteurCost: number
  /** Aantal monteurs met een kostprijs-snapshot dat meetelde in het gemiddelde. */
  monteurCount: number
  rows: PackageMarginRowDTO[]
}

// ─── B. Bezettingsgraad ───────────────────────────────────────────────────────

export interface UtilizationRowDTO {
  employeeId: string
  name: string
  functie: string | null
  plannedHours: number
  availableHours: number
  utilizationPct: number
  aboveTarget: boolean
}

export interface UtilizationDTO {
  /** Target-lijn in % (default 85). */
  targetPct: number
  periodStart: string
  periodEnd: string
  rows: UtilizationRowDTO[]
}

// ─── C. Omzet/kosten/winst prognose ───────────────────────────────────────────

export interface RevenueForecastRowDTO {
  period: string
  revenue: number
  cost: number
  profit: number
  marginPercentage: number
}

export interface RevenueForecastDTO {
  rows: RevenueForecastRowDTO[]
  totals: {
    revenue: number
    cost: number
    profit: number
    marginPercentage: number
  }
}

// ─── Capaciteit per medewerker ────────────────────────────────────────────────

export interface CapacityRowDTO {
  employeeId: string
  name: string
  functie: string | null
  /** Team/afdeling (Department) — groepeer-as `team`. */
  team: string | null
  /** Entiteit/BV — groepeer-as `entiteit`. */
  entiteit: string | null
  costPerHour: number
  pakket: string | null
  ploeg: string | null
  plannedHours: number
  /** Reistijd-uren of `null` wanneer geen reisdata in de periode (UI: '—'). */
  travelHours: number | null
  /** Gereden km of `null` wanneer geen reisdata in de periode (UI: '—'). */
  km: number | null
  revenue: number
  cost: number
  profit: number
  marginPercentage: number
}

// Spec §2.6.2: "Groepeerbaar per: functie, team, pakket, entiteit."
//  - `team`     → Department van de medewerker (`Employee.departmentId`); v3 kent
//    geen teamleider-relatie, department is de team-proxy (consistent met de
//    dashboard-capaciteit + Dienst-eigenaar elders in de codebase).
//  - `entiteit` → BV/Entity van de medewerker (`Employee.entityId`).
export const CAPACITY_GROUP_BY = ['none', 'functie', 'team', 'pakket', 'entiteit'] as const
export type CapacityGroupBy = (typeof CAPACITY_GROUP_BY)[number]

export interface CapacityGroupDTO {
  /** Groeperingssleutel ('Storingsmonteur', 'Service', …) of 'Alle'. */
  group: string
  rows: CapacityRowDTO[]
  totals: {
    revenue: number
    cost: number
    profit: number
    marginPercentage: number
  }
}

export interface ManagementCapacityDTO {
  period: string
  periodStart: string
  periodEnd: string
  groupBy: CapacityGroupBy
  groups: CapacityGroupDTO[]
  totals: {
    revenue: number
    cost: number
    profit: number
    marginPercentage: number
  }
}

// ─── Query schemas ────────────────────────────────────────────────────────────

/**
 * `?period=YYYY-MM` (maand) of `?period=YYYY-Qn` (kwartaal) of `?period=YYYY`
 * (jaar). Default = lopende maand (route bepaalt 'm in Europe/Amsterdam).
 */
export const managementPeriodQuerySchema = z.object({
  period: z
    .string()
    .regex(/^\d{4}(-(0[1-9]|1[0-2]|Q[1-4]))?$/, 'period moet YYYY, YYYY-MM of YYYY-Qn zijn')
    .optional(),
})
export type ManagementPeriodQuery = z.infer<typeof managementPeriodQuerySchema>

export const managementCapacityQuerySchema = managementPeriodQuerySchema.extend({
  groupBy: z.enum(CAPACITY_GROUP_BY).default('none'),
})
export type ManagementCapacityQuery = z.infer<typeof managementCapacityQuerySchema>
